import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import * as Profile from "@/components/auth/Profile";
import { devTools } from "@/components/debug-panel/DevToolsStore";
import { Option, OptionContent, OptionRoot, OptionTrailing } from "@/components/dropdown/Option";
import { ExpressionRealNodePreview } from "@/components/expression/preview/ExpressionNodePreview";
import { SuggestionIcon } from "@/components/suggestions/icon/SuggestionIcon";
import {
  getSuggestionSubTitle,
  getSuggestionTitle,
  libraryRingClass,
} from "@/components/suggestions/list/utils";
import { useListItemProps } from "@/components/suggestions/navigation/useListItemProps";
import { useScrollActiveIntoView } from "@/components/suggestions/navigation/useScrollActiveIntoView";
import { ariaKeyShortcuts, displayHotkey } from "@/utils/hotkeys";
import { twMerge } from "@/utils/twMerge";

import type { UserShort } from "@/api";
import type { Suggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { SuggestionsModel } from "@/models/SuggestionsModel";

type Props = Omit<React.ComponentProps<"button">, "title"> & {
  index: number;
  doc: DocumentModel;
  suggestion: Suggestion;
  suggestions: SuggestionsModel;
  isHighlighted?: boolean;
  isFocused?: boolean;
  hotkey?: string;
  /** The `hotkey` in `aria-keyshortcuts` syntax, when it is one of our shortcuts. */
  ariaHotkey?: string;
  action?: React.ReactNode;
  listId: string | null;
};

export const SuggestionOption = observer(function SuggestionOption({
  ref: forwardedRef,
  doc,
  suggestions,
  suggestion,
  isHighlighted,
  isFocused,
  hotkey,
  ariaHotkey,
  action,
  index,
  listId,
  ...props
}: Props) {
  const t = useTranslations("suggestions");
  const isActive = suggestions.isActive(index, listId);

  const title = getSuggestionTitle(suggestion, t);
  const subTitle = getSuggestionSubTitle(suggestion, t);

  const titleWithNodePreview =
    suggestion.nodePreviewPlacement === "afterTitle" ? (
      <span className="inline-flex items-center gap-x-1.5">
        {title}
        <ExpressionRealNodePreview renderContext="suggestion" suggestion={suggestion} doc={doc} />
      </span>
    ) : (
      title
    );

  // The subtitle, usually a category like "(Subscript)", tells apart items that share a title.
  const label =
    typeof title === "string"
      ? [title, typeof subTitle === "string" ? subTitle : undefined].filter(Boolean).join(" ")
      : undefined;

  const code = "item" in suggestion ? suggestion.item.code : undefined;

  const getItemProps = useListItemProps({
    index,
    listId,
    suggestion,
    isActive,
  });

  const ref = useScrollActiveIntoView(isActive, suggestions, forwardedRef);

  const isKeyed = isActive || isFocused;
  const displayedHotkey = isKeyed ? displayHotkey("active-suggestion") : hotkey;
  const keyShortcuts = isKeyed ? ariaKeyShortcuts("active-suggestion") : ariaHotkey;

  const actionEl = (
    <Action
      action={
        devTools.showPriorities
          ? `${suggestion.matchedString ?? ""}/${suggestion.priority.toFixed(2)}`
          : action
      }
      code={code}
      hotkey={displayedHotkey}
      isActive={isActive}
    />
  );

  if (suggestion.type === "mention") {
    return (
      <OptionRoot
        ref={ref}
        isActive={isActive}
        isHighlighted={isHighlighted}
        isFocused={isFocused}
        disabled={suggestion.disabled}
        checked={suggestion.checked}
        disableHoverStyles
        className="gap-x-1.5 py-1 pl-1.25 pr-2.5 sm:gap-x-0 sm:p-0 sm:px-2"
        {...getItemProps(props)}
      >
        <Profile.Userpic
          name={suggestion.user.name}
          src={suggestion.user.avatarUrls?.[64]}
          className="size-8.5"
        />
        <OptionContent>
          <MentionOption user={suggestion.user} />
        </OptionContent>
        <OptionTrailing
          action={actionEl}
          isActive={isActive}
          checked={suggestion.checked}
          actionClassName="min-w-0 text-[13px] text-gray-500/80"
        />
      </OptionRoot>
    );
  }

  return (
    <Option
      ref={ref}
      icon={suggestion.hideIcon ? undefined : <SuggestionIcon suggestion={suggestion} doc={doc} />}
      title={titleWithNodePreview}
      label={label}
      subTitle={subTitle}
      action={actionEl}
      keyShortcuts={keyShortcuts}
      isActive={isActive}
      isHighlighted={isHighlighted}
      isFocused={isFocused}
      disabled={suggestion.disabled}
      checked={suggestion.checked}
      className={twMerge("fromSm:min-h-7.5 fromSm:pl-1.5", libraryRingClass(suggestion))}
      withSeparator={suggestions.pointerAnchor === null}
      disableHoverStyles
      {...getItemProps(props)}
    />
  );
});

type ActionProps = {
  code?: string;
  hotkey?: string;
  isActive?: boolean;
  action?: React.ReactNode;
};

function Action({ action, code, hotkey, isActive }: ActionProps) {
  if (action) {
    return action;
  }

  if (code && hotkey) {
    return (
      <>
        <span className={twMerge("font-mono", isActive && "text-node-active")}>{code}</span> ⋅{" "}
        {hotkey}
      </>
    );
  }

  return code || hotkey;
}

function MentionOption({ user }: { user: UserShort }) {
  return (
    <div className="flex flex-col leading-[1.2] sm:gap-y-0.5 sm:p-2 sm:leading-normal">
      <span className="text-[15px] font-semibold sm:text-[16px] sm:font-medium">{user.name}</span>
      {user.username && (
        <span className="text-sm text-gray-500 dark:text-gray-400 sm:text-xs">
          @{user.username}
        </span>
      )}
    </div>
  );
}
